import { AsyncPipe, DatePipe, NgFor, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, computed, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import dayjs, { Dayjs } from 'dayjs';
import { BehaviorSubject, combineLatest, map, switchMap } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { NextIcon, PrevIcon, TodayIcon } from 'src/app/core/ui/icons';
import { LearnService } from '../learn.service';
import { LearnPlannerComponent } from './learn-planner/learn-planner.component';

@Component({
  selector: 'app-learn-page',
  standalone: true,
  imports: [
    AsyncPipe,
    DatePipe,
    LearnPlannerComponent,
    NgIf,
    NgFor,
    FontAwesomeModule
  ],
  templateUrl: './learn-page.component.html',
  styleUrls: ['./learn-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LearnPageComponent {
  private route = inject(ActivatedRoute);
  private learnService = inject(LearnService);

  icons = {
    prevIcon: PrevIcon,
    nextIcon: NextIcon,
    todayIcon: TodayIcon
  };

  private currentDate$ = new BehaviorSubject<string>(new Date().toISOString().split('T')[0]);
  private weeksToShow = 1;

  defaultSettings = {
    uid: 'public',
    weeks: 1,
    state: {}
  };

  plannerLink$ = this.route.paramMap.pipe(
    map(params => params.get('plannerLink')),
    switchMap(plannerLink => this.learnService.getPlannerLink(plannerLink || ''))
  );

  weeks$ = this.currentDate$.pipe(
    map(date => {
      const weeks = [];
      const startDate = dayjs(date).startOf('week');  // This will be Sunday

      for (let w = 0; w < this.weeksToShow; w++) {
        const week = {
          start: startDate.add(w, 'week').add(1, 'day'),  // Move to Monday
          days: [] as Dayjs[]
        };

        for (let d = 0; d < 5; d++) {
          week.days.push(startDate.add(w, 'week').add(d + 1, 'day'));  // +1 to start from Monday
        }

        weeks.push(week);
      }

      return weeks;
    })
  );

  data$ = combineLatest([
    this.plannerLink$,
    this.weeks$
  ]).pipe(
    map(([plannerLink, weeks]) => {
      return {
        plannerLink, weeks
      }
    })
  );

  private currentDateSignal = toSignal(this.currentDate$, { initialValue: new Date().toISOString().split('T')[0] });

  canNavigateNext = computed(() => {
    const currentDate = new Date(this.currentDateSignal());
    const nextWeek = new Date(currentDate);
    nextWeek.setDate(nextWeek.getDate() + 7);
    
    const today = new Date();
    const oneWeekFromToday = new Date(today);
    oneWeekFromToday.setDate(oneWeekFromToday.getDate() + 7);
    
    return nextWeek <= oneWeekFromToday;
  });

  goToToday() {
    this.currentDate$.next(new Date().toISOString().split('T')[0]);
  }

  goToNext() {
    const currentDate = new Date(this.currentDate$.value);
    const nextWeek = new Date(currentDate);
    nextWeek.setDate(nextWeek.getDate() + 7);
    
    // Check if next week would be more than one week ahead of today
    const today = new Date();
    const oneWeekFromToday = new Date(today);
    oneWeekFromToday.setDate(oneWeekFromToday.getDate() + 7);
    
    // Only allow navigation if the next week's start is not beyond one week from today
    if (nextWeek <= oneWeekFromToday) {
      this.currentDate$.next(nextWeek.toISOString().split('T')[0]);
    }
  }

  goToPrevious() {
    const currentDate = new Date(this.currentDate$.value);
    currentDate.setDate(currentDate.getDate() - 7);
    this.currentDate$.next(currentDate.toISOString().split('T')[0]);
  }
}
